import { useEffect, useMemo, useState } from 'react';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type {
    ComingDataItem,
    LeadCloudFileType,
    SupplierRequestComingTransferDest,
    SupplierRequestComingType,
    SupplierRequestFullType,
    WarehouseComingRowType,
} from '@econtractor/types';
import * as apiSupplierRequests from 'api/apiSupplierRequests';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { uploadFilesWithOverallProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { safeFloatParse, safeIntParse } from 'utils/helpers/safeParsing';
import type { WarehouseInfo } from '@econtractor/types';
import {
    ALL_COMING_OBJECTS_KEY,
    buildComingDestOptions,
    buildComingObjectOptions,
    comingMatchesObjectFilter,
    itemMatchesComingObject,
    type ComingDest,
} from './comingObject';
import type { SupplierRequestDraftItem } from './types';

interface UseSupplierRequestComingParams {
    requestId: number | null;
    items: SupplierRequestDraftItem[];
    comings: SupplierRequestComingType[];
    canOps: boolean;
    isActive: boolean;
    /** Дата створення заявки (ms) → у опис операції приходу */
    requestDt?: number | null;
    onSuccess: (request: SupplierRequestFullType) => void;
    onListChanged: () => void;
}

const useSupplierRequestComing = ({
    requestId,
    items,
    comings,
    canOps,
    isActive,
    requestDt = null,
    onSuccess,
    onListChanged,
}: UseSupplierRequestComingParams) => {
    const { t } = useTranslation(['Vendors', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const canCredit = Boolean(params.creditControl);
    const rate = Number(localSettings?.RATE?.current || 1) || 1;

    const [showDraft, setShowDraft] = useState(false);
    const [draftComing, setDraftComing] = useState<WarehouseComingRowType[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [pendingCloudFiles, setPendingCloudFiles] = useState<File[]>([]);
    const [cloudProgress, setCloudProgress] = useState<number | boolean>(false);
    const [selectedObjectKey, setSelectedObjectKey] = useState(ALL_COMING_OBJECTS_KEY);
    const [comingDest, setComingDest] = useState<ComingDest>('main');
    const [transferringComingId, setTransferringComingId] = useState<number | null>(null);

    const objectOptions = useMemo(() => buildComingObjectOptions(items), [items]);
    const selectedObjectOption =
        selectedObjectKey > 0
            ? objectOptions.find((option) => (option.objectId || 0) === selectedObjectKey) || null
            : null;
    const destOptions = useMemo(
        () =>
            buildComingDestOptions(
                selectedObjectKey,
                localSettings?.WAREHOUSES as WarehouseInfo[] | undefined,
                selectedObjectOption?.objectStatus,
            ),
        [selectedObjectKey, localSettings?.WAREHOUSES, selectedObjectOption?.objectStatus],
    );

    useEffect(() => {
        setShowDraft(false);
        setDraftComing([]);
        setPendingCloudFiles([]);
        setCloudProgress(false);
        setTransferringComingId(null);
        setSelectedObjectKey(ALL_COMING_OBJECTS_KEY);
        setComingDest('main');
    }, [requestId]);

    useEffect(() => {
        if (!destOptions.includes(comingDest)) setComingDest('main');
    }, [destOptions, comingDest]);

    const selectedItems = useMemo(
        () => items.filter((item) => itemMatchesComingObject(item, selectedObjectKey)),
        [items, selectedObjectKey],
    );

    const remainingByItem = useMemo(() => {
        const map = new Map<number, number>();
        for (const item of selectedItems) {
            const requestItemId = safeIntParse(item.ID) || 0;
            const remaining =
                (safeIntParse(item.itemCount) || 0) - (safeIntParse(item.receivedCount) || 0);
            if (requestItemId > 0 && remaining > 0) map.set(requestItemId, remaining);
        }
        return map;
    }, [selectedItems]);

    const hasSelectedRemaining = remainingByItem.size > 0;
    const formBusy = isSubmitting || cloudProgress !== false || transferringComingId != null;
    const canStartComing = canOps && isActive && hasSelectedRemaining && !formBusy;

    const buildComingDescription = () => {
        const dt = DT.fromMillis(Number(requestDt || 0));
        const date = dt.isValid ? dt.toFormat('dd.MM.yyyy') : '—';
        return t('supplierRequests.comingAutoDescription', { date });
    };

    const buildDraftFromRemaining = () => {
        const cDescription = buildComingDescription();
        const rows: WarehouseComingRowType[] = selectedItems
            .map((item) => {
                const productId = safeIntParse(item.productId) || 0;
                const requestItemId = safeIntParse(item.ID) || 0;
                const remaining =
                    (safeIntParse(item.itemCount) || 0) -
                    (safeIntParse(item.receivedCount) || 0);
                if (!(productId > 0) || !(requestItemId > 0) || !(remaining > 0)) return null;
                const price = safeFloatParse(item.prodDefPrice) || 0;
                const priceSale = safeFloatParse(item.prodDefPriceSale) || 0;
                const prodName = String(item.prodName || '');
                const objectLabel = String(item.objectName || item.clientName || '').trim();
                const showObjectInName = selectedObjectKey === ALL_COMING_OBJECTS_KEY;
                return {
                    setId: 0,
                    requestItemId,
                    product: {
                        ID: productId,
                        value: productId,
                        label:
                            showObjectInName && objectLabel
                                ? `${prodName} · ${objectLabel}`
                                : prodName,
                        vendor: String(item.vendorName || ''),
                        article: String(item.prodArticle || ''),
                        defPrice: price,
                        defPriceSale: priceSale,
                    },
                    coming: {
                        cCount: remaining,
                        cDescription,
                        price,
                        priceRate: safeFloatParse(price * rate) || 0,
                        priceSale,
                        priceSaleRate: safeFloatParse(priceSale * rate) || 0,
                        payment: canCredit,
                        paymentDescription: '',
                        paymentAttachmentsCloud: [],
                    },
                } as WarehouseComingRowType;
            })
            .filter(Boolean) as WarehouseComingRowType[];
        return rows;
    };

    const clearPendingFiles = () => {
        setPendingCloudFiles([]);
        setCloudProgress(false);
    };

    const handleSelectObject = (key: number) => {
        if (showDraft || formBusy) return;
        setSelectedObjectKey(key);
        setComingDest('main');
    };

    const handleSelectDest = (dest: ComingDest) => {
        if (formBusy) return;
        setComingDest(dest);
    };

    const handleOpenDraft = () => {
        if (!canStartComing) return;
        const rows = buildDraftFromRemaining();
        if (!rows.length) {
            showToast(t('supplierRequests.messNoRemaining'), 'warning');
            return;
        }
        clearPendingFiles();
        setDraftComing(rows);
        setShowDraft(true);
    };

    const handleCancelDraft = () => {
        if (isSubmitting || cloudProgress !== false || transferringComingId != null) return;
        setShowDraft(false);
        setDraftComing([]);
        clearPendingFiles();
    };

    const getMaxCount = (row: WarehouseComingRowType) => {
        const requestItemId = safeIntParse(row?.requestItemId) || 0;
        const max = remainingByItem.get(requestItemId);
        return max != null ? max : null;
    };

    const uploadCloudFiles = async (productId: number): Promise<LeadCloudFileType[]> => {
        if (!pendingCloudFiles.length) return [];

        const filesMeta = pendingCloudFiles.map((file) => ({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            fileSize: file.size,
        }));

        const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {
            productId: Number(productId || 0),
        });
        if (!uploadConfigs || uploadConfigs.length !== pendingCloudFiles.length) {
            throw new Error('Upload URLs are not available');
        }

        const uploaded = await uploadFilesWithOverallProgress(
            pendingCloudFiles,
            uploadConfigs,
            setCloudProgress,
        );
        setCloudProgress(100);
        return uploaded.filter((item) => item.success).map((item) => item.config);
    };

    const handleConduct = async () => {
        const id = safeIntParse(requestId) || 0;
        if (!id || formBusy || !draftComing.length) return;

        const validated: Array<{
            row: WarehouseComingRowType;
            productId: number;
            requestItemId: number;
            count: number;
            payment: boolean;
            price: number;
            priceRate: number;
        }> = [];

        for (const row of draftComing) {
            const productId = safeIntParse(row.product?.ID || row.product?.value) || 0;
            const requestItemId = safeIntParse(row.requestItemId) || 0;
            const count = safeIntParse(row.coming?.cCount) || 0;
            const max = remainingByItem.get(requestItemId) ?? 0;
            if (!(productId > 0) || !(requestItemId > 0) || !(count > 0)) {
                showToast(t('supplierRequests.messInputCount'), 'warning');
                return;
            }
            if (count > max) {
                showToast(t('supplierRequests.messComingExceeds'), 'warning');
                return;
            }
            const payment = canCredit && Boolean(row.coming?.payment);
            const price = safeFloatParse(row.coming?.price) || 0;
            const priceRate =
                safeFloatParse(row.coming?.priceRate ?? price * rate) || 0;
            validated.push({ row, productId, requestItemId, count, payment, price, priceRate });
        }

        const hasPaid = validated.some((item) => item.payment);
        let paymentAttachmentsCloud: LeadCloudFileType[] = [];

        setIsSubmitting(true);
        try {
            if (hasPaid && pendingCloudFiles.length) {
                const firstPaidProductId =
                    validated.find((item) => item.payment)?.productId || 0;
                try {
                    setCloudProgress(1);
                    paymentAttachmentsCloud = await uploadCloudFiles(firstPaidProductId);
                    if (paymentAttachmentsCloud.length !== pendingCloudFiles.length) {
                        showToast(t('common:messError'), 'warning');
                    }
                } catch (e) {
                    console.error(e);
                    showToast(t('common:messError'), 'danger');
                    return;
                } finally {
                    setCloudProgress(false);
                }
            }

            const payload: ComingDataItem[] = validated.map(
                ({ row, productId, requestItemId, count, payment, price, priceRate }) => ({
                    productId,
                    requestItemId,
                    count,
                    description: String(row.coming?.cDescription || '').trim(),
                    price,
                    priceRate,
                    priceSale: safeFloatParse(row.coming?.priceSale) || 0,
                    priceSaleRate: safeFloatParse(row.coming?.priceSaleRate) || 0,
                    payment,
                    paymentAmount: payment ? count * price : 0,
                    paymentAmountRate: payment ? count * priceRate : 0,
                    paymentDescription: payment
                        ? String(row.coming?.paymentDescription || '').trim()
                        : '',
                    paymentAttachments: [],
                    paymentAttachmentsCloud: payment ? paymentAttachmentsCloud : [],
                    setId: safeIntParse(row.setId) || 0,
                }),
            );

            const result = await apiSupplierRequests.createComing(id, payload);
            if (!result?.stat || !result.request) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            showToast(t('supplierRequests.messComingDone'), 'success');
            setShowDraft(false);
            setDraftComing([]);
            clearPendingFiles();
            onSuccess(result.request);
            onListChanged();

            const shouldTransfer = comingDest === 'object' || comingDest === 'reserve';
            const newestComing = (result.request.comings || [])[0] || null;
            if (
                shouldTransfer &&
                newestComing &&
                comingMatchesObjectFilter(newestComing.objectId, selectedObjectKey)
            ) {
                await runTransferComing(newestComing, comingDest);
            }
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setIsSubmitting(false);
            setCloudProgress(false);
        }
    };

    const showTransferError = (errCode?: string | null) => {
        switch (errCode) {
            case 'NO_PRODUCT_STOCK':
                showToast(t('messNoProductsStock'), 'danger');
                break;
            case 'WAREHOUSE_CLOSED':
                showToast(t('messWarehouseClosed'), 'danger');
                break;
            case 'ALREADY_TRANSFERRED':
                showToast(t('supplierRequests.messComingAlreadyTransferred'), 'warning');
                break;
            case 'NO_RESERVE_WAREHOUSE':
                showToast(t('supplierRequests.messNoReserveWarehouse'), 'warning');
                break;
            case 'NO_OBJECT_WAREHOUSE':
                showToast(t('supplierRequests.messNoObjectWarehouse'), 'warning');
                break;
            default:
                showToast(t('common:messError'), 'danger');
        }
    };

    const runTransferComing = async (
        coming: SupplierRequestComingType,
        dest: SupplierRequestComingTransferDest,
    ) => {
        const id = safeIntParse(requestId) || 0;
        const comingId = safeIntParse(coming.ID) || 0;
        if (!id || !comingId || !canOps) return;

        const dt = DT.fromMillis(Number(coming.dt || 0));
        const date = dt.isValid ? dt.toFormat('dd.MM.yyyy') : '—';
        const description = t('supplierRequests.transferAutoDescription', { date });

        setTransferringComingId(comingId);
        try {
            const result = await apiSupplierRequests.transferComing(id, comingId, {
                dest,
                description,
            });
            if (!result?.stat || !result.request) {
                showTransferError(result?.errCode);
                return;
            }
            showToast(t('supplierRequests.messComingTransferred'), 'success');
            onSuccess(result.request);
            onListChanged();
        } catch (e) {
            console.error(e);
            showToast(t('common:messError'), 'danger');
        } finally {
            setTransferringComingId(null);
        }
    };

    const handleTransferComing = async (
        coming: SupplierRequestComingType,
        dest: SupplierRequestComingTransferDest,
    ) => {
        if (formBusy) return;
        await runTransferComing(coming, dest);
    };

    return {
        canCredit,
        canStartComing,
        canOps,
        showDraft,
        draftComing,
        setDraftComing,
        isSubmitting,
        pendingCloudFiles,
        setPendingCloudFiles,
        cloudProgress,
        getMaxCount,
        handleOpenDraft,
        handleCancelDraft,
        handleConduct,
        objectOptions,
        selectedObjectKey,
        handleSelectObject,
        comingDest,
        destOptions,
        selectedObjectOption,
        handleSelectDest,
        transferringComingId,
        handleTransferComing,
        formBusy,
    };
};

export default useSupplierRequestComing;
